/**
 * Finance data access: rider earnings and company earnings.
 *
 * Read-only. Types come from the shared contract, so a field the API stops
 * sending is a compile error rather than an empty cell.
 */

import type { ScopeFilter } from '../../lib/locationContext';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  CompanyEarnings,
  CompanyEarningsKind,
  CompanyEarningsPeriod,
  CompanyEarningsTransactions,
  DeliveryOrigin,
  FinanceDelivery,
  RiderEarningsReport,
  DailyRiderEarningsDelivery,
  DailyRiderEarningsReport,
} from '@cenox/shared';
import { api } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

export const qs = (params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
  const s = search.toString();
  return s ? `?${s}` : '';
};

export interface EarningsFilters {
  from: string;
  to: string;
  riderId?: string | undefined;
  branchId?: string | undefined;
  country?: string | undefined;
  state?: string | undefined;
  source?: DeliveryOrigin | undefined;
}

export function useRiderEarnings(filters: EarningsFilters, enabled = true): UseQueryResult<RiderEarningsReport> {
  const path = useBusinessPath();
  return useQuery({
    enabled,
    queryKey: ['finance', 'riderEarnings', path(''), filters],
    queryFn: () =>
      api.get<RiderEarningsReport>(path(`/finance/rider-earnings${qs({ ...filters })}`)),
    staleTime: 30_000,
  });
}

export function useRiderEarningsDeliveries(
  target: { date: string; riderId: string | null; riderName: string } | null,
  filters: Omit<EarningsFilters, 'from' | 'to' | 'riderId'>,
): UseQueryResult<{ data: FinanceDelivery[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['finance', 'riderEarningsDeliveries', path(''), target, filters],
    queryFn: () =>
      api.get<{ data: FinanceDelivery[] }>(
        path(
          `/finance/rider-earnings/deliveries${qs({
            date: target!.date,
            ...(target!.riderId ? { riderId: target!.riderId } : { riderName: target!.riderName }),
            ...filters,
          })}`,
        ),
      ),
    enabled: target !== null,
  });
}

export function useCompanyEarnings(input: {
  period: CompanyEarningsPeriod;
  from?: string | undefined;
  to?: string | undefined;
  /** The sidebar's geographic scope. */
  scope?: ScopeFilter | undefined;
  /** False until the sidebar's scope has settled, so nothing whole-business flashes past. */
  scopeReady?: boolean;
}): UseQueryResult<CompanyEarnings> {
  const path = useBusinessPath();
  const ready =
    (input.scopeReady ?? true) && (input.period !== 'custom' || (Boolean(input.from) && Boolean(input.to)));
  return useQuery({
    queryKey: ['finance', 'companyEarnings', path(''), input],
    queryFn: () =>
      api.get<CompanyEarnings>(
        path(
          `/finance/company-earnings${qs({
            period: input.period,
            ...(input.period === 'custom' ? { from: input.from, to: input.to } : {}),
            ...input.scope,
          })}`,
        ),
      ),
    enabled: ready,
    staleTime: 30_000,
  });
}

export function useCompanyEarningsTransactions(
  target: { kind: CompanyEarningsKind; currency: string } | null,
  range: { from: string; to: string; scope?: ScopeFilter | undefined },
): UseQueryResult<CompanyEarningsTransactions> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['finance', 'companyEarningsTransactions', path(''), target, range],
    queryFn: () =>
      api.get<CompanyEarningsTransactions>(
        path(
          `/finance/company-earnings/transactions${qs({
            kind: target!.kind,
            currency: target!.currency,
            from: range.from,
            to: range.to,
            ...range.scope,
          })}`,
        ),
      ),
    enabled: target !== null,
  });
}

/** Daily rider earnings for one service date, under the sidebar's scope. */
export function useDailyRiderEarnings(
  filters: { date: string; search?: string | undefined } & Record<string, string | undefined>,
  enabled = true,
): UseQueryResult<DailyRiderEarningsReport> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['finance', 'dailyRiderEarnings', path(''), filters],
    queryFn: () => api.get<DailyRiderEarningsReport>(path(`/finance/daily-rider-earnings${qs({ ...filters })}`)),
    enabled,
    staleTime: 30_000,
  });
}

/** One rider's counted deliveries on that date — the same filters as the report. */
export function useDailyRiderEarningsDeliveries(
  target: { riderKey: string; currencyCode: string } | null,
  filters: { date: string; search?: string | undefined } & Record<string, string | undefined>,
): UseQueryResult<{ data: DailyRiderEarningsDelivery[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['finance', 'dailyRiderEarningsDeliveries', path(''), target, filters],
    queryFn: () =>
      api.get<{ data: DailyRiderEarningsDelivery[] }>(
        path(`/finance/daily-rider-earnings/deliveries${qs({ ...filters, riderKey: target!.riderKey, currencyCode: target!.currencyCode })}`),
      ),
    enabled: target !== null,
  });
}
